import {SkillOriginEnum, SkillTypeEnum} from '@api';
import type {ExperienceSkillOrigin, ExperienceSkillType} from '~/types/api-raw-types';
import type {Experience} from '~/types/domain/Experiences';
import type {ExperienceRequest} from './ExperienceRequest';

class ExperienceMapper {
    toRequest(experience: Experience, index: number): ExperienceRequest {
        return {
            index,
            area: experience.area || undefined,
            asFreelance: experience.asFreelance,
            commentCount: experience.commentCount,
            company: experience.company,
            companyId: experience.companyId,
            currentJob: experience.currentJob,
            description: experience.description,
            endDate: experience.endDate ? new Date(experience.endDate) : undefined,
            id: experience.id,
            location: experience.location,
            logo: experience.logo,
            logoUrl: experience.logoUrl,
            organizationName: experience.organizationName,
            recommendationCount: experience.recommendationCount,
            startDate: experience.startDate ? new Date(experience.startDate) : undefined,
            missionId: experience.missionId,
            skills: experience.skills?.map((skill) => ({
                id: skill.id,
                label: skill.label,
                type: this.typeToRequest(skill.type),
                origin: this.originToRequest(skill.origin),
            })),
            title: experience.title,
        };
    }

    typeToRequest(type: ExperienceSkillType): SkillTypeEnum {
        switch (type) {
            case 'GLOBAL':
                return SkillTypeEnum.Global;
            case 'FUNCTIONAL':
                return SkillTypeEnum.Functional;
            default:
                throw new Error(`Unknown ExperienceSkillType: ${type}`);
        }
    }

    originToRequest(origin: ExperienceSkillOrigin): SkillOriginEnum {
        switch (origin) {
            case 'MANUAL':
                return SkillOriginEnum.Manual;
            case 'SUGGESTION':
                return SkillOriginEnum.Suggestion;
            default:
                throw new Error(`Unknown ExperienceSkillOrigin: ${origin}`);
        }
    }

    sortByDate(a: ExperienceRequest, b: ExperienceRequest) {
        const endDateA = a.endDate && !a.currentJob ? new Date(a.endDate) : null;
        const endDateB = b.endDate && !b.currentJob ? new Date(b.endDate) : null;

        if (endDateA === null && endDateB === null && a.startDate && b.startDate) {
            return b.startDate.getTime() - a.startDate.getTime();
        } else if (endDateA === null) {
            return -1;
        } else if (endDateB === null) {
            return 1;
        }

        if ((endDateA.getMonth(), endDateA.getFullYear()) === (endDateB.getMonth(), endDateB.getFullYear())) {
            if (a.startDate && b.startDate) return b.startDate?.getTime() - a.startDate?.getTime();
        }

        return endDateB.getTime() - endDateA.getTime();
    }
}

export const experienceMapper = new ExperienceMapper();
